import React, { useState, useEffect, FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import styles from './LoginPage.module.css';

type Stage = 'loading' | 'redirecting' | 'form' | 'success' | 'invalid';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Supabase processes the token from the URL hash and fires PASSWORD_RECOVERY.
    // We get a short window to try redirecting to the native app first.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        // Try to open the native app via deep link, passing the tokens in the fragment.
        // If the app is installed on this device, iOS/Android will open it.
        // If not (desktop browser), we fall back to the web form after a short delay.
        const appUrl =
          `beryl://reset-password#access_token=${session.access_token}` +
          `&refresh_token=${session.refresh_token}&type=recovery`;

        setStage('redirecting');
        window.location.href = appUrl;

        // After 2.5s, if still here, the app wasn't installed — show the web form instead.
        setTimeout(() => {
          setStage((s) => (s === 'redirecting' ? 'form' : s));
        }, 2500);
      }
    });

    // Fallback: if already have a session (page reload), go straight to form
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setStage((s) => (s === 'loading' ? 'form' : s));
      } else {
        // No session and no PASSWORD_RECOVERY event after 3s → link is invalid/expired
        setTimeout(() => {
          setStage((s) => (s === 'loading' ? 'invalid' : s));
        }, 3000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError(null);
    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (updateError) {
      setError(updateError.message);
    } else {
      setStage('success');
    }
  };

  if (stage === 'loading' || stage === 'redirecting') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.branding}>
            <div className={styles.appName}>beryl</div>
          </div>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: 8 }}>
            {stage === 'redirecting' ? 'Opening beryl…' : 'Verifying your reset link…'}
          </p>
          {stage === 'redirecting' && (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginTop: 8 }}>
              If the app doesn't open automatically, you can reset your password below.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (stage === 'invalid') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.branding}>
            <div className={styles.appName}>beryl</div>
          </div>
          <div className={styles.successBox}>
            <div className={styles.successIcon}>⚠</div>
            <h2 className={styles.successTitle}>Link expired</h2>
            <p className={styles.successBody}>
              This password reset link is invalid or has expired. Please request a new one in the app.
            </p>
          </div>
          <button className={styles.submitButton} onClick={() => navigate('/')}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.branding}>
            <div className={styles.appName}>beryl</div>
          </div>
          <div className={styles.successBox}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.successTitle}>Password updated</h2>
            <p className={styles.successBody}>
              Your password has been changed. You can now sign in with your new password.
            </p>
          </div>
          <button className={styles.submitButton} onClick={() => navigate('/')}>
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.branding}>
          <div className={styles.appName}>beryl</div>
          <div className={styles.subtitle}>Your Recovery Buddy</div>
        </div>

        <h1 className={styles.title}>Set New Password</h1>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error ? <div className={styles.error}>{error}</div> : null}

          <div className={styles.inputGroup}>
            <label className={styles.label}>New Password</label>
            <input
              className={styles.input}
              type="password"
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              minLength={6}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>Confirm Password</label>
            <input
              className={styles.input}
              type="password"
              placeholder="Repeat your new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button className={styles.submitButton} type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
